import { get } from "lodash";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ComponentType, Suspense, useMemo } from "react";
import Loader from "../../components/Loader";
import constants from "../../utils/constants";
import classNames from "classnames";
import { isWebView } from "../../utils";
import { t } from "i18next";
import { Authenticated } from "@qure/qure-auth";

interface SettingsPageProps {
  pageName?: string;
  onChangeSetting?: (pageName: string) => void;
}

const getComponent = (currentPageName: string) => {
  let _component: any = null;

  switch (currentPageName) {
    case constants.SETTINGS.NOTIFICATIONS:
      _component = import("../../components/NotificationSettings");
      break;
    case constants.SETTINGS.AUTOMATED_SHARING:
      _component = import("../../components/AutomatedShare");
      break;
    case constants.SETTINGS.USER_GROUPS:
      _component = import("../../components/UserGroupManagement");
      break;
    case constants.SETTINGS.WORKLIST_TAGS:
      _component = import("../../components/WorklistTagsSetting");
      break;
    case constants.SETTINGS.FORM_TEMPLATES:
      _component = import("../../components/FormSettings");
      break;
    case constants.SETTINGS.WORKSPACE_MANAGEMENT:
      _component = import("../../components/WorkspaceManagement");
      break;
    case constants.SETTINGS.SITE_MANAGEMENT:
      _component = import("../../components/WorkspaceManagement");
      break;
    case constants.SETTINGS.REPORT_TEMPLATE:
      _component = import("../../components/ReportTemplate");
      break;
    case constants.SETTINGS.GATEWAY:
      _component = import("../../components/GatewayIndicator");
      break;
    case constants.SETTINGS.MODALITY_CONNECT:
      _component = import("../../components/ConnectModality");
      break;
    case constants.SETTINGS.DISK_MANAGEMENT:
      _component = import("../../components/DiskManagement");
      break;
    case constants.SETTINGS.REMOTE_ACCESS:
      _component = import("../../components/RemoteAccess");
      break;
    case constants.SETTINGS.LICENSE_MANAGER:
      _component = import("../../components/LicenseManager");
      break;
    case constants.SETTINGS.SCAN_TO_CONNECT:
      _component = import("../../components/ScanToConnect");
      break;
    case constants.SETTINGS.EXPORT_DATA:
      _component = import("../../components/ExportData");
      break;
    case constants.SETTINGS.CUSTOM_TAGS_MANAGEMENT:
      _component = import("../../components/CustomTagsManagement");
      break;
    default:
      _component = null;
  }

  // Handle null component case to prevent TypeError
  if (_component === null) {
    return dynamic(
      () =>
        Promise.resolve(() => (
          <div className="text-gray-900 dark:text-white">{t("page_not_found")}</div>
        )),
      {
        ssr: false,
        loading: () => <Loader size="medium" label={`${t("loading_key")}...`} />,
      }
    );
  }

  return dynamic(() => _component, {
    ssr: false,
    loading: () => <Loader size="medium" label={`${t("loading_key")}...`} />,
  });
};

const SettingsPage = (props: SettingsPageProps) => {
  const { pageName, onChangeSetting } = props;
  const router = useRouter();
  const currentPageName = pageName || get(router, ["query", "params", "0"], "");
  const DynamicComponent: ComponentType<any> = useMemo(
    () => getComponent(currentPageName),
    [currentPageName]
  );

  const onChangeSettingPage = (_pageName: string, pageNo: number) => {
    router.replace(`/settings/${_pageName}${pageNo ? "/" + pageNo : ""}`, undefined, {
      shallow: true,
    });
  };
  return (
    <div
      className={classNames(
        "dark:bg-gray-900 sm:bg-gray-200 sm:dark:bg-gray-800 w-full flex flex-col h-full items-center justify-center overflow-x-hidden",
        {
          "pt-6": isWebView(),
        }
      )}
    >
      <Suspense fallback={`${t("loading_key")}...`}>
        <Authenticated>
          <DynamicComponent
            pageName={currentPageName}
            onChangeSetting={onChangeSetting || onChangeSettingPage}
          />
        </Authenticated>
      </Suspense>
    </div>
  );
};

export default SettingsPage;

export async function getStaticProps() {
  return {
    // Passed to the page component as props
    props: {
      hideMenu: true,
    },
  };
}

export const getStaticPaths = () => {
  return {
    paths: [], //indicates that no page needs be created at build time
    fallback: "blocking", //indicates the type of fallback
  };
};
